import React, { useState, useEffect } from 'react';
import { ChevronRight, ChevronDown, Workflow } from 'lucide-react';
import logger from '../../utils/logger';
import projectService from '../../services/projectService';
import { useAuth } from '../../AuthProvider';
import { canFinalize } from '../../utils/roles';
import SetWorkflowModal from '../modals/SetWorkflowModal';
import YoutubeLinkCard from '../synopsis/detail/YoutubeLinkCard';
import OverviewCard from '../synopsis/detail/OverviewCard';
import LeaveNoteCard from '../synopsis/detail/LeaveNoteCard';
import TranscriptionReadOnlyBox from './detail/TranscriptionReadOnlyBox';
import KeywordsReadOnlyBox from './detail/KeywordsReadOnlyBox';
import EditableTranscriptBox from './detail/EditableTranscriptBox';

const KeywordProjectDetailPage = ({ project, details, onClose, onDelete, onRefresh }) => {
  const { user } = useAuth();
  const canDeleteProject = user?.role === 'admin' || user?.app_metadata?.role === 'admin';
  const canApproveReject = user?.role === 'admin' || user?.role === 'pm' || user?.app_metadata?.role === 'admin' || user?.app_metadata?.role === 'pm';
  const workflowStatus = project?.workflowStatus ?? project?.workflow_status ?? details?.project?.workflow_status;
  const currentUserIsAssignee = details?.current_user_is_assignee === true;
  const canEditContent = currentUserIsAssignee || canApproveReject; // PM/Admin can always edit
  const showAcceptButton = workflowStatus === 'ready_for_review' && currentUserIsAssignee;
  const showSubmitButton = (workflowStatus === 'in_progress' || workflowStatus === 'rejected') && currentUserIsAssignee;
  const showApproveButton = workflowStatus === 'ready_for_pm_review' && canApproveReject;
  const showRejectButton = workflowStatus === 'ready_for_pm_review' && canApproveReject;
  const showDeliverButton = workflowStatus === 'pm_approved' && canApproveReject;
  const [accepting, setAccepting] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [approving, setApproving] = useState(false);
  const [rejecting, setRejecting] = useState(false);
  const [delivering, setDelivering] = useState(false);
  const [workflowModalOpen, setWorkflowModalOpen] = useState(false);
  const canSetWorkflow = user?.role === 'admin' || user?.role === 'pm' || user?.app_metadata?.role === 'admin' || user?.app_metadata?.role === 'pm';
  const isDelivered = workflowStatus === 'delivered';
  const handleAccept = async () => {
    if (!project?.id || accepting) return;
    setAccepting(true);
    try {
      await projectService.updateWorkflowStatus(project.id, 'in_progress');
      if (onRefresh) await onRefresh();
    } catch (err) {
      console.error('Accept failed:', err);
      alert(err.response?.data?.detail || 'Failed to accept task');
    } finally {
      setAccepting(false);
    }
  };
  const handleSubmit = async () => {
    if (!project?.id || submitting) return;
    setSubmitting(true);
    try {
      await projectService.updateWorkflowStatus(project.id, 'ready_for_pm_review');
      if (onRefresh) await onRefresh();
    } catch (err) {
      console.error('Submit failed:', err);
      alert(err.response?.data?.detail || 'Failed to submit');
    } finally {
      setSubmitting(false);
    }
  };
  const handleApprove = async () => {
    if (!project?.id || approving) return;
    setApproving(true);
    try {
      await projectService.updateWorkflowStatus(project.id, 'pm_approved');
      if (onRefresh) await onRefresh();
    } catch (err) {
      console.error('Approve failed:', err);
      alert(err.response?.data?.detail || 'Failed to approve');
    } finally {
      setApproving(false);
    }
  };
  const handleReject = async () => {
    if (!project?.id || rejecting) return;
    if (!window.confirm('Reject this submission? It will go back to the reviewer.')) return;
    setRejecting(true);
    try {
      await projectService.updateWorkflowStatus(project.id, 'rejected');
      if (onRefresh) await onRefresh();
    } catch (err) {
      console.error('Reject failed:', err);
      alert(err.response?.data?.detail || 'Failed to reject');
    } finally {
      setRejecting(false);
    }
  };
  const handleDeliver = async () => {
    if (!project?.id || delivering) return;
    setDelivering(true);
    try {
      await projectService.updateWorkflowStatus(project.id, 'delivered');
      if (onRefresh) await onRefresh();
    } catch (err) {
      console.error('Deliver failed:', err);
      alert(err.response?.data?.detail || 'Failed to deliver');
    } finally {
      setDelivering(false);
    }
  };
  const metadata = details?.metadata || project?.metadata;
  const keywords = details?.keywords || project?.keywords;
  const transcript = details?.transcript || project?.transcript;

  // Share with user: Google STT or Whisper. Only Reviewer+ can change it. User gets transcript/keywords for this source.
  const [userSharedSource, setUserSharedSource] = useState(() => {
    const fromDetails = details?.user_shared_source || details?.project?.keyword_user_shared_source;
    if (fromDetails === 'whisper' || fromDetails === 'google_stt') return fromDetails;
    try {
      const stored = localStorage.getItem(`keyword_user_shared_${project?.id}`);
      return stored === 'whisper' ? 'whisper' : 'google_stt';
    } catch {
      return 'google_stt';
    }
  });
  const [showUserSharedDropdown, setShowUserSharedDropdown] = useState(false);
  const [isUpdatingUserShared, setIsUpdatingUserShared] = useState(false);

  useEffect(() => {
    const fromDetails = details?.user_shared_source || details?.project?.keyword_user_shared_source;
    if (fromDetails === 'whisper' || fromDetails === 'google_stt') setUserSharedSource(fromDetails);
  }, [details?.user_shared_source, details?.project?.keyword_user_shared_source]);

  useEffect(() => {
    if (!project?.id) return;
    try {
      localStorage.setItem(`keyword_user_shared_${project.id}`, userSharedSource);
    } catch (e) {
      logger.warn('Could not persist user shared source', e);
    }
  }, [project?.id, userSharedSource]);

  const handleUserSharedChange = async (source) => {
    if (source === userSharedSource) return;
    setShowUserSharedDropdown(false);
    setIsUpdatingUserShared(true);
    try {
      await projectService.setKeywordUserShared(project.id, source);
      setUserSharedSource(source);
      if (onRefresh) await onRefresh();
    } catch (err) {
      logger.error('Error setting user shared source:', err);
      alert(err?.response?.data?.detail || 'Failed to update. Try again.');
    } finally {
      setIsUpdatingUserShared(false);
    }
  };

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showUserSharedDropdown && !e.target.closest('.keyword-user-shared-dropdown')) setShowUserSharedDropdown(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showUserSharedDropdown]);

  // Both versions always visible; reviewer-approved one gets green highlight in editable box
  const googleSttTranscript = transcript;
  const googleSttKeywords = keywords;
  const whisperTranscript = details?.transcript_whisper ?? project?.transcript_whisper ?? null;
  const whisperKeywords = details?.keywords_whisper ?? project?.keywords_whisper ?? null;

  return (
    <div className="flex flex-col h-full bg-background">
      {/* Header */}
      <div className="bg-card border-b border-border shadow-subtle-sm flex-shrink-0 h-15">
        <div className="px-4 py-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <button
                onClick={onClose}
                className="p-2 hover:bg-muted rounded-lg transition-colors"
                title="Back to projects"
              >
                <ChevronRight className="w-6 h-6 text-muted-foreground rotate-180" />
              </button>
              <div>
                <h1 className="text-foreground text-xl font-semibold">
                  {project?.title || project?.name || 'Keyword Project'}
                </h1>
                <p className="text-xs text-muted-foreground mt-0.5">
                  Workflow: {project?.workflowStatusDisplayName ?? project?.workflow_status_display_name ?? details?.project?.workflow_status_display_name ?? '—'}
                </p>
              </div>
            </div>

            {/* Right: Accept (assignee) | Share with user | Delete */}
            <div className="flex items-center space-x-2">
              {showAcceptButton && (
                <button
                  type="button"
                  onClick={handleAccept}
                  disabled={accepting}
                  className="bg-primary hover:bg-primary/90 text-background px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
                >
                  {accepting ? 'Accepting…' : 'Accept'}
                </button>
              )}
              {showSubmitButton && (
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={submitting}
                  className="bg-primary hover:bg-primary/90 text-background px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
                >
                  {submitting ? 'Submitting…' : 'Submit'}
                </button>
              )}
              {showApproveButton && (
                <button
                  type="button"
                  onClick={handleApprove}
                  disabled={approving}
                  className="bg-primary hover:bg-primary/90 text-background px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
                >
                  {approving ? 'Approving…' : 'Approve'}
                </button>
              )}
              {showRejectButton && (
                <button
                  type="button"
                  onClick={handleReject}
                  disabled={rejecting}
                  className="bg-destructive text-destructive-foreground border border-border px-4 py-2 rounded-lg text-sm font-medium disabled:opacity-50"
                >
                  {rejecting ? 'Rejecting…' : 'Reject'}
                </button>
              )}
              {showDeliverButton && (
                <button
                  type="button"
                  onClick={handleDeliver}
                  disabled={delivering}
                  className="bg-primary hover:bg-primary/90 text-background px-4 py-2 rounded-lg text-sm font-medium transition-colors disabled:opacity-50"
                >
                  {delivering ? 'Sending…' : 'Deliver to Creator'}
                </button>
              )}
              {canFinalize(user) ? (
                <div className="relative keyword-user-shared-dropdown">
                  <button
                    type="button"
                    onClick={() => !isUpdatingUserShared && setShowUserSharedDropdown(!showUserSharedDropdown)}
                    disabled={isUpdatingUserShared}
                    title="Which transcript version to share with user"
                    className={`min-w-[100px] border border-border px-4 py-2 rounded-lg text-sm font-medium flex items-center justify-between transition-colors focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0 ${isUpdatingUserShared ? 'bg-muted text-muted-foreground cursor-default' : 'bg-card text-card-foreground hover:bg-muted'}`}
                  >
                    <span>{userSharedSource === 'whisper' ? 'Whisper' : 'Google STT'}</span>
                    {!isUpdatingUserShared && <ChevronDown className="w-4 h-4 ml-1" />}
                  </button>
                  {showUserSharedDropdown && (
                    <div className="absolute right-0 mt-2 w-48 bg-popover border border-border rounded-lg shadow-subtle-sm z-50">
                      <div className="py-1">
                        <button
                          type="button"
                          onClick={() => handleUserSharedChange('google_stt')}
                          className="w-full text-left px-4 py-2 text-sm text-popover-foreground hover:bg-muted"
                        >
                          Google STT
                        </button>
                        <button
                          type="button"
                          onClick={() => handleUserSharedChange('whisper')}
                          className="w-full text-left px-4 py-2 text-sm text-popover-foreground hover:bg-muted"
                        >
                          Whisper
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <span className="text-xs text-muted-foreground" title="Only a reviewer or above can set what is shared with the user">
                  Share with user: {userSharedSource === 'whisper' ? 'Whisper' : 'Google STT'}
                </span>
              )}
              {canSetWorkflow && !isDelivered && (
                <button
                  type="button"
                  onClick={() => setWorkflowModalOpen(true)}
                  className="border border-border bg-card text-card-foreground hover:bg-muted px-4 py-2 rounded-lg text-sm font-medium flex items-center gap-2"
                >
                  <Workflow className="w-4 h-4" /> Set Workflow
                </button>
              )}
              {canDeleteProject && (
                <button
                  type="button"
                  onClick={() => {
                    if (window.confirm('Are you sure you want to delete this project?')) {
                      onDelete(project.id);
                    }
                  }}
                  className="bg-destructive text-destructive-foreground border border-border px-4 py-2 rounded-lg text-sm font-medium flex items-center focus:outline-none focus:ring-0 focus-visible:outline-none focus-visible:ring-0"
                >
                  DELETE
                </button>
              )}
            </div>
          </div>
        </div>
      </div>

      <SetWorkflowModal
        isOpen={workflowModalOpen}
        onClose={() => setWorkflowModalOpen(false)}
        projectId={project?.id}
        projectType={project?.projectType ?? project?.project_type}
        isLocked={isDelivered}
        onSaved={onRefresh}
      />

      {/* Content */}
      <div className="flex-1 overflow-y-auto">
        <div className="p-4 pb-8">
          <div className="space-y-4">
            {/* Top: Youtube link (optional) */}
            <YoutubeLinkCard metadata={metadata} />

            {/* Main area: Overview + Leave note side by side, then Google STT row, then Whisper row */}
            <div className="space-y-4">
              {/* Row 1: Overview and Leave note beside each other */}
              <div className="grid grid-cols-2 gap-4">
                <OverviewCard
                  project={project}
                  metadata={metadata}
                  transcript={transcript}
                  currentSynopsis={keywords}
                />
                <LeaveNoteCard projectId={project?.id} initialNote={project?.notes || details?.notes || ''} />
              </div>

              {/* Row 2: Google STT – always visible. Pastel green on editable box when this is the reviewer-approved version. */}
              <div>
                <h2 className="text-foreground text-sm font-semibold mb-2">Google STT</h2>
                <div className="grid grid-cols-3 gap-4">
                  <TranscriptionReadOnlyBox transcript={googleSttTranscript} title="Transcription" />
                  <KeywordsReadOnlyBox keywords={googleSttKeywords} title="Extracted keywords" />
                  <EditableTranscriptBox
                    transcript={googleSttTranscript}
                    projectId={project?.id}
                    onSaved={onRefresh}
                    title="Editable transcript"
                    disabled={false}
                    readOnly={isDelivered || !canEditContent}
                    source="google_stt"
                    highlightGreen={false}
                  />
                </div>
              </div>

              {/* Row 3: Whisper – always visible when data exists */}
              <div>
                <h2 className="text-foreground text-sm font-semibold mb-2">Whisper</h2>
                <div className="grid grid-cols-3 gap-4">
                  <TranscriptionReadOnlyBox transcript={whisperTranscript} title="Transcription" />
                  <KeywordsReadOnlyBox keywords={whisperKeywords} title="Extracted keywords" />
                  <EditableTranscriptBox
                    transcript={whisperTranscript}
                    projectId={project?.id}
                    onSaved={onRefresh}
                    title="Editable transcript"
                    disabled={!whisperTranscript}
                    readOnly={isDelivered || !canEditContent}
                    source="whisper"
                    highlightGreen={false}
                  />
                </div>
                {!whisperTranscript && (
                  <p className="text-muted-foreground text-xs mt-2">
                    Whisper transcript not yet available. Keywords are extracted with Gemini from both transcripts when available.
                  </p>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

    </div>
  );
};

export default KeywordProjectDetailPage;
